export function defaultReminderCategories() {
  return [
    {
      id: 'before-due',
      type: 'beforeDue',
      name: 'Reminder',
      daysBefore: 7,
      enabled: true,
    },
    {
      id: 'overdue',
      type: 'overdue',
      name: 'Overdue',
      untilPaid: true,
      enabled: true,
    },
  ];
}

export const DEFAULT_REMINDER_MAIL = {
  enabled: false,
  toAddresses: '',
  time: '08:00',
  timezone: 'America/New_York',
  categories: defaultReminderCategories(),
};

export const DEFAULT_MAIL_FETCH_RULES = {
  fromAddresses: '',
  subjectKeywords: '',
  hasAttachmentsOnly: false,
  newerThanDays: 60,
  syncMode: 'manual',
  reminderMail: {
    ...DEFAULT_REMINDER_MAIL,
    categories: defaultReminderCategories(),
  },
};

export const MAIL_SYNC_MODES = [
  {
    value: 'manual',
    label: 'Manual',
    description: 'Load mail when you refresh. Choose how far back below.',
    icon: 'bi bi-hand-index',
  },
  {
    value: 'daily',
    label: 'Every day',
    description: 'Check once daily for new matching bill mail.',
    icon: 'bi bi-calendar-day',
  },
  {
    value: 'immediate',
    label: 'Immediate',
    description: 'Live sync — inbox updates while Draft Bills is open (no Refresh).',
    icon: 'bi bi-lightning-charge',
  },
];

export const RECEIVED_PRESETS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 120, label: '120 days' },
  { value: 0, label: 'Any time' },
];

/** Lookback used for actual mail fetch (daily/immediate ignore manual Received). */
export function effectiveNewerThanDays(rules) {
  const mode = normalizeSyncMode(rules?.syncMode);
  if (mode === 'daily') return 2;
  if (mode === 'immediate') return 1;
  const days = Number(rules?.newerThanDays);
  return Number.isFinite(days) && days >= 0 ? days : DEFAULT_MAIL_FETCH_RULES.newerThanDays;
}

export function parseAddressList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (!value || typeof value !== 'string') return [];
  return value
    .split(/[,;\n]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function clampDaysBefore(value) {
  const days = Number(value);
  if (!Number.isFinite(days)) return 7;
  return Math.min(90, Math.max(1, Math.round(days)));
}

function normalizeReminderTime(value) {
  const match = String(value || '08:00').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return '08:00';
  const hour = Math.min(23, Math.max(0, Number(match[1])));
  const minute = Math.min(59, Math.max(0, Number(match[2])));
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function normalizeReminderCategories(value, legacyWhen) {
  const raw = Array.isArray(value) ? value : [];
  const categories = raw
    .map((item, index) => {
      const type = item?.type === 'overdue' ? 'overdue' : 'beforeDue';
      return {
        id: String(item?.id || `${type}-${index + 1}`),
        type,
        name: String(item?.name || (type === 'overdue' ? 'Overdue' : 'Reminder')).trim() || (type === 'overdue' ? 'Overdue' : 'Reminder'),
        daysBefore: type === 'beforeDue' ? clampDaysBefore(item?.daysBefore) : null,
        untilPaid: type === 'overdue' ? item?.untilPaid !== false : false,
        enabled: item?.enabled !== false,
      };
    })
    .slice(0, 8);

  if (categories.length) return categories;

  const when = Array.isArray(legacyWhen) ? legacyWhen : ['beforeDue', 'overdue'];
  const fallback = [];
  if (when.includes('beforeDue') || when.includes('dueToday')) {
    fallback.push({
      id: 'before-due',
      type: 'beforeDue',
      name: 'Reminder',
      daysBefore: when.includes('dueToday') && !when.includes('beforeDue') ? 1 : 7,
      enabled: true,
    });
  }
  if (when.includes('overdue') || !fallback.length) {
    fallback.push({
      id: 'overdue',
      type: 'overdue',
      name: 'Overdue',
      untilPaid: true,
      enabled: true,
    });
  }
  return fallback.length ? fallback : defaultReminderCategories();
}

export function normalizeReminderMail(value) {
  const raw = value && typeof value === 'object' ? value : {};
  return {
    enabled: !!raw.enabled,
    toAddresses: parseAddressList(raw.toAddresses).join(', '),
    time: normalizeReminderTime(raw.time),
    timezone: String(raw.timezone || 'America/New_York').trim() || 'America/New_York',
    categories: normalizeReminderCategories(raw.categories, raw.when),
  };
}

function normalizeSyncMode(value) {
  const mode = String(value || '').trim().toLowerCase();
  if (mode === 'daily' || mode === 'immediate' || mode === 'manual') return mode;
  return DEFAULT_MAIL_FETCH_RULES.syncMode;
}

export function normalizeMailFetchRules(data) {
  const fromList = parseAddressList(data?.fromAddresses);
  const subjectList = parseAddressList(data?.subjectKeywords);
  const days = Number(data?.newerThanDays);

  return {
    id: data?.id ?? null,
    companyId: data?.companyId ?? null,
    fromAddresses: fromList.join(', '),
    subjectKeywords: subjectList.join(', '),
    hasAttachmentsOnly: !!data?.hasAttachmentsOnly,
    newerThanDays: Number.isFinite(days) && days >= 0 ? days : DEFAULT_MAIL_FETCH_RULES.newerThanDays,
    syncMode: normalizeSyncMode(data?.syncMode),
    reminderMail: normalizeReminderMail(data?.reminderMail),
  };
}

export function toMailFetchSettingsPayload(rules) {
  return {
    fromAddresses: parseAddressList(rules?.fromAddresses),
    subjectKeywords: parseAddressList(rules?.subjectKeywords),
    hasAttachmentsOnly: !!rules?.hasAttachmentsOnly,
    newerThanDays: Math.min(365, Math.max(0, Number(rules?.newerThanDays) || 0)),
    syncMode: normalizeSyncMode(rules?.syncMode),
    reminderMail: {
      enabled: !!rules?.reminderMail?.enabled,
      toAddresses: parseAddressList(rules?.reminderMail?.toAddresses),
      time: normalizeReminderTime(rules?.reminderMail?.time),
      timezone: String(rules?.reminderMail?.timezone || 'America/New_York').trim() || 'America/New_York',
      categories: normalizeReminderCategories(rules?.reminderMail?.categories, rules?.reminderMail?.when),
    },
  };
}

export function hasConfiguredMailSources(rules) {
  return parseAddressList(rules?.fromAddresses).length > 0;
}

export function isValidMailSource(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;

  if (trimmed.startsWith('@')) {
    return /^@[a-z0-9.-]+\.[a-z]{2,}$/i.test(trimmed);
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

function buildSubjectPart(keywords, service) {
  if (!keywords.length) return '';

  if (keywords.length === 1) {
    return service === 'gmail'
      ? `subject:(${keywords[0]})`
      : `subject:${keywords[0]}`;
  }

  if (service === 'gmail') {
    return `subject:(${keywords.join(' OR ')})`;
  }

  return `(${keywords.map((word) => `subject:${word}`).join(' OR ')})`;
}

function buildAddressGroup(field, addresses) {
  if (!addresses.length) return '';
  if (addresses.length === 1) return `${field}:${addresses[0]}`;
  return `(${addresses.map((addr) => `${field}:${addr}`).join(' OR ')})`;
}

/**
 * Build provider-specific inbox query from configured rules.
 * Outlook uses SEARCH: prefix for Microsoft Graph $search.
 */
export function buildMailFetchQuery(rules, service) {
  const froms = parseAddressList(rules?.fromAddresses);
  if (!froms.length) return '';

  const subjects = parseAddressList(rules?.subjectKeywords);
  const parts = [buildAddressGroup('from', froms)];

  const subjectPart = buildSubjectPart(subjects, service);
  if (subjectPart) parts.push(subjectPart);

  if (rules?.hasAttachmentsOnly) {
    parts.push(service === 'gmail' ? 'has:attachment' : 'hasattachment:yes');
  }

  const days = effectiveNewerThanDays(rules);
  if (days > 0) {
    if (service === 'gmail') {
      parts.push(`newer_than:${days}d`);
    } else {
      const since = new Date();
      since.setDate(since.getDate() - days);
      parts.push(`received>=${since.toISOString().slice(0, 10)}`);
    }
  }

  const searchText = parts.join(' ').trim();
  return service === 'outlook' ? `SEARCH:${searchText}` : searchText;
}

export function describeMailFetchRules(rules) {
  const froms = parseAddressList(rules?.fromAddresses);
  if (!froms.length) return 'No senders configured';

  const mode = normalizeSyncMode(rules?.syncMode);
  const bits = [`From ${froms.length} sender${froms.length > 1 ? 's' : ''}`];
  const subjects = parseAddressList(rules?.subjectKeywords);
  if (subjects.length) bits.push(`Subject: ${subjects.slice(0, 2).join(', ')}`);
  if (rules?.hasAttachmentsOnly) bits.push('With attachments');

  if (mode === 'daily') {
    bits.push('Daily sync');
  } else if (mode === 'immediate') {
    bits.push('Immediate sync');
  } else {
    const days = effectiveNewerThanDays(rules);
    if (days > 0) bits.push(`Last ${days} days`);
    else bits.push('Any received date');
    bits.push('Manual refresh');
  }

  return bits.join(' · ');
}

/** @deprecated use hasConfiguredMailSources */
export function hasActiveFetchRules(rules) {
  return hasConfiguredMailSources(rules);
}
